// features/chat/chatService.ts
//
// Client helpers for the per-tournament staff chat. Messages live in RTDB at
// tournamentChats/{tid}/channels/{cid}/messages; the server-side push fan-out
// is functions/src/chat.ts. Read markers are per-user at chatReads/{uid}.

import { ref, push, update, set, onValue, query, limitToLast, Unsubscribe } from 'firebase/database';
import { db } from '../../config/firebase';
import { getServerTime } from '../../utils/serverTime';
import { CHAT_CHANNEL_IDS, ChatChannelId, ConversationMeta, ConversationStatus } from './channels';

export interface ChatMessage {
  id: string;
  uid: string;
  name: string;
  role?: string;
  text: string;
  ts: number;
}

export interface ChannelSummary {
  lastText?: string;
  lastTs?: number;
  lastName?: string;
  unread: boolean;
  meta?: ConversationMeta;
}

/** A single user's global technical-support thread (admin inbox row). */
export interface SupportRequest {
  requesterUid: string;
  requesterName: string;
  lastText?: string;
  lastTs?: number;
  lastName?: string;
  meta?: ConversationMeta;
  unread: boolean;
}

const MAX_LEN = 2000;

export interface ChatSender {
  uid: string;
  name: string;
  role?: string;
}

// ── Path helpers ─────────────────────────────────────────────────────────────
export function channelBase(tournamentId: string, channelId: ChatChannelId): string {
  return `tournamentChats/${tournamentId}/channels/${channelId}`;
}
export function supportBase(requesterUid: string): string {
  return `globalSupport/${requesterUid}`;
}

export function conversationStatus(meta?: ConversationMeta): ConversationStatus {
  return meta?.status || 'open';
}

/**
 * Post a message (single atomic multi-path write): the message, the
 * denormalized lastMessage (powers overview badges), the sender as a channel
 * participant, and the sender's own read marker.
 */
export async function sendChatMessage(
  tournamentId: string,
  channelId: ChatChannelId,
  text: string,
  sender: ChatSender,
): Promise<void> {
  const trimmed = text.trim().slice(0, MAX_LEN);
  if (!trimmed) return;
  const ts = getServerTime();
  const base = `tournamentChats/${tournamentId}/channels/${channelId}`;
  const key = push(ref(db, `${base}/messages`)).key as string;
  await update(ref(db), {
    [`${base}/messages/${key}`]: {
      uid: sender.uid,
      name: sender.name,
      ...(sender.role ? { role: sender.role } : {}),
      text: trimmed,
      ts,
    },
    [`${base}/lastMessage`]: { text: trimmed, ts, name: sender.name },
    [`${base}/participants/${sender.uid}`]: true,
    [`chatReads/${sender.uid}/${tournamentId}/${channelId}`]: ts,
  });
}

/** Mark a channel read up to now for a user. */
export async function markChatRead(
  uid: string,
  tournamentId: string,
  channelId: ChatChannelId,
): Promise<void> {
  await set(ref(db, `chatReads/${uid}/${tournamentId}/${channelId}`), getServerTime());
}

/** Live messages for one channel (most recent 200). */
export function subscribeChannelMessages(
  tournamentId: string,
  channelId: ChatChannelId,
  cb: (messages: ChatMessage[]) => void,
): Unsubscribe {
  const q = query(
    ref(db, `tournamentChats/${tournamentId}/channels/${channelId}/messages`),
    limitToLast(200),
  );
  return onValue(q, (snap) => {
    const out: ChatMessage[] = [];
    snap.forEach((child) => {
      const v = (child.val() || {}) as Partial<ChatMessage>;
      out.push({
        id: child.key as string,
        uid: v.uid || '',
        name: v.name || 'Staff',
        role: v.role,
        text: v.text || '',
        ts: v.ts || 0,
      });
      return false;
    });
    cb(out);
  });
}

/**
 * Live per-channel summaries (last message + unread flag) for one tournament.
 * Combines the channels' lastMessage with the user's read markers.
 */
export function subscribeTournamentChannelSummaries(
  tournamentId: string,
  uid: string,
  cb: (summaries: Record<ChatChannelId, ChannelSummary>) => void,
): Unsubscribe {
  let channels: Record<
    string,
    { lastMessage?: { text?: string; ts?: number; name?: string }; meta?: ConversationMeta }
  > = {};
  let reads: Record<string, number> = {};

  const emit = () => {
    const out = {} as Record<ChatChannelId, ChannelSummary>;
    for (const id of CHAT_CHANNEL_IDS) {
      const lm = channels[id]?.lastMessage;
      const readTs = reads[id] || 0;
      out[id] = {
        lastText: lm?.text,
        lastTs: lm?.ts,
        lastName: lm?.name,
        unread: !!(lm?.ts && lm.ts > readTs),
        meta: channels[id]?.meta,
      };
    }
    cb(out);
  };

  const u1 = onValue(ref(db, `tournamentChats/${tournamentId}/channels`), (s) => {
    channels = (s.val() || {}) as typeof channels;
    emit();
  });
  const u2 = onValue(ref(db, `chatReads/${uid}/${tournamentId}`), (s) => {
    reads = (s.val() || {}) as Record<string, number>;
    emit();
  });

  return () => {
    u1();
    u2();
  };
}

// ── Claim / handoff (CS-ticket lifecycle) ────────────────────────────────────
// All operate on a conversation "base path": a tournament request channel
// (channelBase) or the global support line (supportBase).

/** A responder takes ownership: status → claimed, recorded as participant. */
export async function claimConversation(basePath: string, claimer: ChatSender): Promise<void> {
  const ts = getServerTime();
  await update(ref(db), {
    [`${basePath}/meta/status`]: 'claimed',
    [`${basePath}/meta/claimedBy`]: claimer.uid,
    [`${basePath}/meta/claimedByName`]: claimer.name,
    ...(claimer.role ? { [`${basePath}/meta/claimedByRole`]: claimer.role } : {}),
    [`${basePath}/meta/claimedAt`]: ts,
    [`${basePath}/participants/${claimer.uid}`]: true,
  });
}

/** Release a claimed conversation back to the pool (re-alerts responders). */
export async function releaseConversation(basePath: string): Promise<void> {
  await update(ref(db, `${basePath}/meta`), {
    status: 'open',
    claimedBy: null,
    claimedByName: null,
    claimedByRole: null,
    claimedAt: null,
  });
}

/** Close out a conversation. */
export async function resolveConversation(basePath: string): Promise<void> {
  await update(ref(db, `${basePath}/meta`), { status: 'resolved' });
}

/** Reopen a resolved conversation (back to the unclaimed pool). */
export async function reopenConversation(basePath: string): Promise<void> {
  await update(ref(db, `${basePath}/meta`), {
    status: 'open',
    claimedBy: null,
    claimedByName: null,
    claimedByRole: null,
    claimedAt: null,
  });
}

/** Add a user as a participant without claiming (e.g. a second responder). */
export async function joinConversation(basePath: string, uid: string): Promise<void> {
  await set(ref(db, `${basePath}/participants/${uid}`), true);
}

/** Pull admins into a tournament request channel. */
export async function requestAdmin(basePath: string): Promise<void> {
  await update(ref(db, `${basePath}/meta`), {
    adminRequested: true,
    adminRequestedAt: getServerTime(),
  });
}

/** Live meta for a single conversation. */
export function subscribeConversationMeta(
  basePath: string,
  cb: (meta: ConversationMeta) => void,
): Unsubscribe {
  return onValue(ref(db, `${basePath}/meta`), (s) => cb((s.val() || {}) as ConversationMeta));
}

/** Live participant set for a conversation. */
export function subscribeParticipants(
  basePath: string,
  cb: (participants: Record<string, boolean>) => void,
): Unsubscribe {
  return onValue(ref(db, `${basePath}/participants`), (s) =>
    cb((s.val() || {}) as Record<string, boolean>),
  );
}

/**
 * Whether a conversation should still "alert" a given viewer. The requester
 * always sees unread alerts; a responder's alert clears once someone else has
 * claimed it (or it's resolved) — the core "handled, not my open point" rule.
 */
export function isAlerting(
  sum: { unread?: boolean; meta?: ConversationMeta } | undefined,
  viewerIsResponder: boolean,
  viewerUid: string,
): boolean {
  if (!sum?.unread) return false;
  if (!viewerIsResponder) return true;
  const st = conversationStatus(sum.meta);
  if (st === 'resolved') return false;
  if (st === 'claimed' && sum.meta?.claimedBy !== viewerUid) return false;
  return true;
}

// ── Global technical support (one thread per requesting user) ─────────────────

/** Post into a user's global support thread. */
export async function sendSupportMessage(
  requesterUid: string,
  text: string,
  sender: ChatSender,
): Promise<void> {
  const trimmed = text.trim().slice(0, MAX_LEN);
  if (!trimmed) return;
  const ts = getServerTime();
  const base = supportBase(requesterUid);
  const key = push(ref(db, `${base}/messages`)).key as string;
  const updates: Record<string, unknown> = {
    [`${base}/messages/${key}`]: {
      uid: sender.uid,
      name: sender.name,
      ...(sender.role ? { role: sender.role } : {}),
      text: trimmed,
      ts,
    },
    [`${base}/lastMessage`]: { text: trimmed, ts, name: sender.name },
    [`${base}/participants/${sender.uid}`]: true,
    [`chatReads/${sender.uid}/__support/${requesterUid}`]: ts,
  };
  // Stamp the requester's identity (used by the admin inbox) the first time
  // the requester themselves posts.
  if (sender.uid === requesterUid) {
    updates[`${base}/requester`] = { uid: requesterUid, name: sender.name };
  }
  await update(ref(db), updates);
}

/** Live messages for a support thread (most recent 200). */
export function subscribeSupportMessages(
  requesterUid: string,
  cb: (messages: ChatMessage[]) => void,
): Unsubscribe {
  const q = query(ref(db, `${supportBase(requesterUid)}/messages`), limitToLast(200));
  return onValue(q, (snap) => {
    const out: ChatMessage[] = [];
    snap.forEach((child) => {
      const v = (child.val() || {}) as Partial<ChatMessage>;
      out.push({
        id: child.key as string,
        uid: v.uid || '',
        name: v.name || 'Staff',
        role: v.role,
        text: v.text || '',
        ts: v.ts || 0,
      });
      return false;
    });
    cb(out);
  });
}

/** Summary (last message + unread + meta) of one support thread for a viewer. */
export function subscribeSupportSummary(
  requesterUid: string,
  viewerUid: string,
  cb: (s: ChannelSummary) => void,
): Unsubscribe {
  let lastMessage: { text?: string; ts?: number; name?: string } | undefined;
  let meta: ConversationMeta | undefined;
  let readTs = 0;
  const emit = () =>
    cb({
      lastText: lastMessage?.text,
      lastTs: lastMessage?.ts,
      lastName: lastMessage?.name,
      unread: !!(lastMessage?.ts && lastMessage.ts > readTs),
      meta,
    });
  const u1 = onValue(ref(db, `${supportBase(requesterUid)}/lastMessage`), (s) => {
    lastMessage = (s.val() || undefined) as typeof lastMessage;
    emit();
  });
  const u2 = onValue(ref(db, `${supportBase(requesterUid)}/meta`), (s) => {
    meta = (s.val() || undefined) as ConversationMeta | undefined;
    emit();
  });
  const u3 = onValue(ref(db, `chatReads/${viewerUid}/__support/${requesterUid}`), (s) => {
    readTs = (s.val() as number) || 0;
    emit();
  });
  return () => {
    u1();
    u2();
    u3();
  };
}

/** Admin inbox: every support thread, newest first, with unread + claim state. */
export function subscribeOpenSupportRequests(
  viewerUid: string,
  cb: (requests: SupportRequest[]) => void,
): Unsubscribe {
  let threads: Record<
    string,
    {
      lastMessage?: { text?: string; ts?: number; name?: string };
      meta?: ConversationMeta;
      requester?: { uid?: string; name?: string };
    }
  > = {};
  let reads: Record<string, number> = {};
  const emit = () => {
    const out: SupportRequest[] = Object.entries(threads)
      .filter(([, t]) => !!t.lastMessage?.ts)
      .map(([requesterUid, t]) => ({
        requesterUid,
        requesterName: t.requester?.name || t.lastMessage?.name || 'Staff',
        lastText: t.lastMessage?.text,
        lastTs: t.lastMessage?.ts,
        lastName: t.lastMessage?.name,
        meta: t.meta,
        unread: !!(t.lastMessage?.ts && t.lastMessage.ts > (reads[requesterUid] || 0)),
      }))
      .sort((a, b) => (b.lastTs || 0) - (a.lastTs || 0));
    cb(out);
  };
  const u1 = onValue(
    ref(db, 'globalSupport'),
    (s) => {
      threads = (s.val() || {}) as typeof threads;
      emit();
    },
    () => cb([]),
  );
  const u2 = onValue(ref(db, `chatReads/${viewerUid}/__support`), (s) => {
    reads = (s.val() || {}) as Record<string, number>;
    emit();
  });
  return () => {
    u1();
    u2();
  };
}

/** Mark a support thread read for a viewer. */
export async function markSupportRead(viewerUid: string, requesterUid: string): Promise<void> {
  await set(ref(db, `chatReads/${viewerUid}/__support/${requesterUid}`), getServerTime());
}

// ── Shared posting + message/summary readers ─────────────────────────────────

/** Generic atomic post: message + lastMessage + participant + own read marker. */
async function postMessage(
  base: string,
  text: string,
  sender: ChatSender,
  readMarkerPath: string,
  extra?: Record<string, unknown>,
): Promise<void> {
  const trimmed = text.trim().slice(0, MAX_LEN);
  if (!trimmed) return;
  const ts = getServerTime();
  const key = push(ref(db, `${base}/messages`)).key as string;
  await update(ref(db), {
    [`${base}/messages/${key}`]: {
      uid: sender.uid,
      name: sender.name,
      ...(sender.role ? { role: sender.role } : {}),
      text: trimmed,
      ts,
    },
    [`${base}/lastMessage`]: { text: trimmed, ts, name: sender.name },
    [`${base}/participants/${sender.uid}`]: true,
    [readMarkerPath]: ts,
    ...(extra || {}),
  });
}

function subscribeMessagesAt(base: string, cb: (messages: ChatMessage[]) => void): Unsubscribe {
  const q = query(ref(db, `${base}/messages`), limitToLast(200));
  return onValue(q, (snap) => {
    const out: ChatMessage[] = [];
    snap.forEach((child) => {
      const v = (child.val() || {}) as Partial<ChatMessage>;
      out.push({
        id: child.key as string,
        uid: v.uid || '',
        name: v.name || 'Staff',
        role: v.role,
        text: v.text || '',
        ts: v.ts || 0,
      });
      return false;
    });
    cb(out);
  });
}

function subscribeSummaryAt(
  base: string,
  readMarkerPath: string,
  cb: (s: ChannelSummary) => void,
): Unsubscribe {
  let lastMessage: { text?: string; ts?: number; name?: string } | undefined;
  let meta: ConversationMeta | undefined;
  let readTs = 0;
  const emit = () =>
    cb({
      lastText: lastMessage?.text,
      lastTs: lastMessage?.ts,
      lastName: lastMessage?.name,
      unread: !!(lastMessage?.ts && lastMessage.ts > readTs),
      meta,
    });
  const u1 = onValue(ref(db, `${base}/lastMessage`), (s) => {
    lastMessage = (s.val() || undefined) as typeof lastMessage;
    emit();
  });
  const u2 = onValue(ref(db, `${base}/meta`), (s) => {
    meta = (s.val() || undefined) as ConversationMeta | undefined;
    emit();
  });
  const u3 = onValue(ref(db, readMarkerPath), (s) => {
    readTs = (s.val() as number) || 0;
    emit();
  });
  return () => {
    u1();
    u2();
    u3();
  };
}

// ── Floor team chat (one room per floor team) ────────────────────────────────
export function floorBase(tournamentId: string, teamLetter: string): string {
  return `tournamentChats/${tournamentId}/floor/${teamLetter}`;
}
const floorReadKey = (uid: string, tid: string, letter: string): string =>
  `chatReads/${uid}/${tid}/floor_${letter}`;

export async function sendFloorMessage(
  tournamentId: string,
  teamLetter: string,
  text: string,
  sender: ChatSender,
): Promise<void> {
  await postMessage(
    floorBase(tournamentId, teamLetter),
    text,
    sender,
    floorReadKey(sender.uid, tournamentId, teamLetter),
  );
}

export function subscribeFloorMessages(
  tournamentId: string,
  teamLetter: string,
  cb: (messages: ChatMessage[]) => void,
): Unsubscribe {
  return subscribeMessagesAt(floorBase(tournamentId, teamLetter), cb);
}

export function subscribeFloorSummary(
  tournamentId: string,
  teamLetter: string,
  viewerUid: string,
  cb: (s: ChannelSummary) => void,
): Unsubscribe {
  return subscribeSummaryAt(
    floorBase(tournamentId, teamLetter),
    floorReadKey(viewerUid, tournamentId, teamLetter),
    cb,
  );
}

export async function markFloorRead(uid: string, tournamentId: string, teamLetter: string): Promise<void> {
  await set(ref(db, floorReadKey(uid, tournamentId, teamLetter)), getServerTime());
}

/** Floor-team letters + labels for a tournament (for the oversight picker). */
export interface FloorTeamInfo {
  letter: string;
  label: string;
}
export function subscribeFloorTeams(
  tournamentId: string,
  cb: (teams: FloorTeamInfo[]) => void,
): Unsubscribe {
  return onValue(
    ref(db, `tournaments/${tournamentId}/floorTeams`),
    (s) => {
      const data = (s.val() || {}) as Record<string, { isHeadJudgeTeam?: boolean; isFeatureMatchTeam?: boolean; isDeckCheckTeam?: boolean }>;
      const teams = Object.entries(data)
        .map(([letter, t]) => ({
          letter,
          label: t.isHeadJudgeTeam
            ? 'Head Judge team'
            : t.isFeatureMatchTeam
              ? 'Feature Match team'
              : t.isDeckCheckTeam
                ? 'Deck Check team'
                : `Floor team ${letter}`,
        }))
        .sort((a, b) => a.letter.localeCompare(b.letter));
      cb(teams);
    },
    () => cb([]),
  );
}

// ── Reach-a-role request threads (one private thread per requester) ───────────
export function requestBase(tournamentId: string, channelId: ChatChannelId, requesterUid: string): string {
  return `tournamentChats/${tournamentId}/requests/${channelId}/${requesterUid}`;
}
const requestReadKey = (uid: string, tid: string, cid: string, requesterUid: string): string =>
  `chatReads/${uid}/__req/${tid}/${cid}/${requesterUid}`;

export async function sendRequestMessage(
  tournamentId: string,
  channelId: ChatChannelId,
  requesterUid: string,
  text: string,
  sender: ChatSender,
): Promise<void> {
  const base = requestBase(tournamentId, channelId, requesterUid);
  await postMessage(
    base,
    text,
    sender,
    requestReadKey(sender.uid, tournamentId, channelId, requesterUid),
    // Stamp the requester's identity (used by the responder inbox) the first
    // time the requester themselves posts.
    sender.uid === requesterUid ? { [`${base}/requester`]: { uid: requesterUid, name: sender.name } } : undefined,
  );
}

export function subscribeRequestMessages(
  tournamentId: string,
  channelId: ChatChannelId,
  requesterUid: string,
  cb: (messages: ChatMessage[]) => void,
): Unsubscribe {
  return subscribeMessagesAt(requestBase(tournamentId, channelId, requesterUid), cb);
}

export function subscribeRequestSummary(
  tournamentId: string,
  channelId: ChatChannelId,
  requesterUid: string,
  viewerUid: string,
  cb: (s: ChannelSummary) => void,
): Unsubscribe {
  return subscribeSummaryAt(
    requestBase(tournamentId, channelId, requesterUid),
    requestReadKey(viewerUid, tournamentId, channelId, requesterUid),
    cb,
  );
}

export async function markRequestRead(
  viewerUid: string,
  tournamentId: string,
  channelId: ChatChannelId,
  requesterUid: string,
): Promise<void> {
  await set(ref(db, requestReadKey(viewerUid, tournamentId, channelId, requesterUid)), getServerTime());
}

/** Responder inbox: every requester's thread for one tournament + channel. */
export function subscribeRequestInbox(
  tournamentId: string,
  channelId: ChatChannelId,
  viewerUid: string,
  cb: (requests: SupportRequest[]) => void,
): Unsubscribe {
  let threads: Record<
    string,
    {
      lastMessage?: { text?: string; ts?: number; name?: string };
      meta?: ConversationMeta;
      requester?: { uid?: string; name?: string };
    }
  > = {};
  let reads: Record<string, number> = {};
  const emit = () => {
    const out: SupportRequest[] = Object.entries(threads)
      .filter(([, t]) => !!t.lastMessage?.ts)
      .map(([requesterUid, t]) => ({
        requesterUid,
        requesterName: t.requester?.name || t.lastMessage?.name || 'Staff',
        lastText: t.lastMessage?.text,
        lastTs: t.lastMessage?.ts,
        lastName: t.lastMessage?.name,
        meta: t.meta,
        unread: !!(t.lastMessage?.ts && t.lastMessage.ts > (reads[requesterUid] || 0)),
      }))
      .sort((a, b) => (b.lastTs || 0) - (a.lastTs || 0));
    cb(out);
  };
  const u1 = onValue(
    ref(db, `tournamentChats/${tournamentId}/requests/${channelId}`),
    (s) => {
      threads = (s.val() as typeof threads) || {};
      emit();
    },
    () => cb([]),
  );
  const u2 = onValue(ref(db, `chatReads/${viewerUid}/__req/${tournamentId}/${channelId}`), (s) => {
    reads = (s.val() as Record<string, number>) || {};
    emit();
  });
  return () => {
    u1();
    u2();
  };
}
